import { defineDesignedMap } from "./_helpers.js";

// "The Gauntlet" — the worked example of a DESIGNED (finite) course, authored
// entirely in this file (no templates borrowed from other maps). It is one long
// level chopped into the usual 30-wide scenes that chain into a single
// continuous canvas: every scene begins and ends on the same baseline floor
// (two solid bottom rows) UNLESS noted, so the seams line up cleanly. The one
// deliberate elevation change is the stairs pair (climb up, then back down),
// whose shared boundary is authored to match.
//
// Difficulty ramps with the sequence order: a calm warm-up, then single hazards
// (spike, gap), then combinations (spikes over pits, bullets + pit), an
// elevation detour, a timed laser gate, and finally a dense spike-and-pit
// finale before the finish runway. Design patterns are borrowed in spirit from
// the base templates (telegraphed single spikes, 3-wide jumpable gaps, a
// contained turret with a left backstop, a raised platform over a chasm) but
// every grid here is hand-authored.
//
// Authoring rules followed (see docs/level-generation.md):
//  - 30 cells wide, bottom-anchored (last row = ground).
//  - Edge columns stay solid so scenes tile without seams.
//  - Pits are 3 cells wide (one jump); spikes sit on solid ground, never stacked.
//  - Every projectile spawner ("P") has a "B" to its left in the same row so its
//    rounds die inside the scene.
//  - A laser in the TOP authored row is a full-height timed gate.

const scenes = {
  // 1 — Warm-up. Flat ground to settle into the run.
  warmup: {
    layout: `
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 2 — First hazard: a single, well-telegraphed spike to jump.
  spike_single: {
    layout: `
..............S...............
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 3 — A spaced pair of spikes: two jumps with room to recover between them.
  spike_pair: {
    layout: `
......S................S......
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 4 — First gaps: two 3-wide pits around a small island. Time your jumps.
  pits_small: {
    layout: `
BBBBBBBBB...BBBBBB...BBBBBBBBB
BBBBBBBBB...BBBBBB...BBBBBBBBB
`,
    metadata: {},
  },

  // 5 — Elevation read: a platform floats over a wide chasm. Hop up onto it and
  // back down to the far ledge (the chasm is lethal — only the platform crosses).
  raised_cross: {
    layout: `
..........BBBBBBBBBB..........
..............................
..............................
..............................
BBBBBB..................BBBBBB
BBBBBB..................BBBBBB
`,
    metadata: {},
  },

  // 6 — Turret intro: a single spawner fires left across the lane; the BB pillar
  // on the left is the backstop that catches its rounds inside the scene.
  turret_intro: {
    layout: `
..BB......................BB..
..BB......................pB..
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 7 — Combination: spikes planted on the islands between two pits, so each
  // landing is also a spike to clear.
  spikes_over_pits: {
    layout: `
....S.........S..........S....
BBBBBBBBB...BBBBBB...BBBBBBBBB
BBBBBBBBB...BBBBBB...BBBBBBBBB
`,
    metadata: {},
  },

  // 8 — Climb. A staircase up to a higher shelf. EXIT floor is raised; the next
  // scene (stairs_down_spikes) is authored to enter at this same height.
  stairs_up: {
    layout: `
........................BBBBBB
..................BBBBBBBBBBBB
............BBBBBBBBBBBBBBBBBB
......BBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 9 — Descend. A staircase back down to the baseline, with a spike waiting on
  // the bottom landing. ENTRY floor is raised to meet stairs_up's exit.
  stairs_down_spikes: {
    layout: `
BBBBBB........................
BBBBBBBBBBBB..................
BBBBBBBBBBBBBBBBBB............
BBBBBBBBBBBBBBBBBBBBBBBB..S...
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 10 — Timed laser gate (top-row "L" = a full-height beam). Flat runway on both
  // sides: adjust your speed so you pass the column while the beam is off.
  laser_gate: {
    layout: `
..............L...............
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 11 — Bullets over a gap: clear the central pit while a turret rakes the lane.
  turret_and_pit: {
    layout: `
..BB......................BB..
..BB......................pB..
BBBBBBBBBBBBBB...BBBBBBBBBBBBB
BBBBBBBBBBBBBB...BBBBBBBBBBBBB
`,
    metadata: {},
  },

  // 12 — Finale: a triple-pit crossing with a spike planted on every island.
  // Precise, back-to-back jumps — the hardest stretch of the course.
  finale: {
    layout: `
...S.........S.........S......
BBBBBB...BBBBBB...BBBBBB...BBB
BBBBBB...BBBBBB...BBBBBB...BBB
`,
    metadata: {},
  },

  // 13 — Finish runway. The finish line is drawn at this scene's right edge;
  // cross it alive to complete the course.
  finish: {
    layout: `
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },
};

export const gauntletMap = defineDesignedMap({
  id: "gauntlet",
  name: "The Gauntlet",
  description:
    "A fixed, hand-built course that ramps from gentle to brutal. Reach the finish line to complete it.",
  scenes,
  sequence: [
    "warmup",
    "spike_single",
    "spike_pair",
    "pits_small",
    "raised_cross",
    "turret_intro",
    "spikes_over_pits",
    "stairs_up",
    "stairs_down_spikes",
    "laser_gate",
    "turret_and_pit",
    "finale",
    "finish",
  ],
});
